import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';

import { z } from 'zod';
import { toast } from 'sonner';
import { toNoonUTC } from '@/shared/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { getIntroIntakeFormApiEndPoint } from '@shared';
import { shortFormIntakeApi } from '@client/api/waterlily';
import { quickPlanPersonSchema, type QuickPlanPersonValues } from '@/features/client-quick-plan';

/**
 * The one New Case Quick Start form. It always creates a plan from the basics; ticking
 * `sendIntakeForm` also emails the client the medical questionnaire.
 *
 * These used to be two separate choices, "No intake form" and "Send medical intake form". An
 * advisor who picked the first and then wanted to send the form had nowhere to add an email, went
 * to Invite Client instead, and ended up with a second client (UX walkthrough, 9 Oct 2026). An
 * email is asked for, and required, only when the form is being sent.
 */
export const intakeFormQuickPlanSchema = quickPlanPersonSchema
  .extend({
    sendIntakeForm: z.boolean().default(false),
    email: z.string().default(''),
    partnerEmail: z.string().default(''),
    hasPartner: z.boolean().default(false),
    partner: z.any().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.sendIntakeForm && !z.string().email().safeParse(data.email).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: data.email ? 'Invalid email address' : 'Client email is required',
        path: ['email'],
      });
    }
    if (data.hasPartner) {
      if (!data.partner) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Partner details are required',
          path: ['partner'],
        });
      } else {
        const result = quickPlanPersonSchema.safeParse(data.partner);
        if (!result.success) {
          for (const issue of result.error.issues) {
            ctx.addIssue({ ...issue, path: ['partner', ...issue.path] });
          }
        }
      }
      if (!data.sendIntakeForm) return;
      if (!data.partnerEmail) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Partner email is required',
          path: ['partnerEmail'],
        });
      } else if (!z.string().email().safeParse(data.partnerEmail).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid email address',
          path: ['partnerEmail'],
        });
      }
    }
  });

export type IntakeFormQuickPlanFormValues = z.infer<typeof intakeFormQuickPlanSchema>;
export type IntakeFormQuickPlanFormInput = z.input<typeof intakeFormQuickPlanSchema>;

interface UseIntakeFormQuickPlanOptions {
  advisorId: string;
  onSuccess?: (data: ShortFormIntakeResponse) => void;
  /** Whether the questionnaire tick box starts ticked. */
  initialSendIntakeForm?: boolean;
}

const PARTNER_ELIGIBLE_STATUSES = ['Married', 'Partnered'];

/**
 * `email` is the address to send the questionnaire to. Without one nothing is emailed, and
 * `skipInviteEmail` says so outright, as the old "No intake form" option did.
 */
export function mapPersonToRequest(
  formValues: QuickPlanPersonValues,
  advisorId: string,
  email?: string,
): ShortFormIntakeRequest {
  const genderMap: Record<string, string> = {
    male: 'Male',
    female: 'Female',
  };

  const smokerMap: Record<string, string> = {
    never: 'No',
    former: 'Yes',
    current: 'Yes',
  };

  const intakeFormUrl = getIntroIntakeFormApiEndPoint(advisorId, false);

  return {
    clientFirstName: formValues.firstName,
    clientLastName: formValues.lastName,
    clientBirthDate: toNoonUTC(formValues.birthDate),
    clientGenderAtBirth: genderMap[formValues.sex] ?? 'Male',
    clientSmokerEver: smokerMap[formValues.smokingStatus] ?? 'No',
    clientMaritalStatus: formValues.maritalStatus,
    clientZipCode: formValues.zipCode,
    ...(email ? { clientEmail: email, skipInviteEmail: false } : { skipInviteEmail: true }),
    intakeFormUrl,
  };
}

export function useIntakeFormQuickPlan({
  advisorId,
  onSuccess,
  initialSendIntakeForm = false,
}: UseIntakeFormQuickPlanOptions) {
  const navigate = useNavigate();
  const lastCoupleLinked = useRef(true);
  const lastCoupleDeliveryFailed = useRef(false);

  const form = useForm<IntakeFormQuickPlanFormInput, unknown, IntakeFormQuickPlanFormValues>({
    resolver: zodResolver(intakeFormQuickPlanSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      zipCode: '',
      maritalStatus: '',
      sendIntakeForm: initialSendIntakeForm,
      email: '',
      partnerEmail: '',
      hasPartner: false,
    },
  });

  const sendIntakeForm = form.watch('sendIntakeForm');

  function handleSendIntakeFormChange(checked: boolean) {
    // `shouldValidate` re-runs the whole schema, so the submit button follows the tick box, but
    // shows errors for this field alone: ticking the box on an empty form must not turn every
    // other field red.
    form.setValue('sendIntakeForm', checked, { shouldDirty: true, shouldValidate: true });
    if (!checked) form.clearErrors(['email', 'partnerEmail']);
  }

  const hasPartner = form.watch('hasPartner');
  const maritalStatus = form.watch('maritalStatus');
  const canAddPartner = PARTNER_ELIGIBLE_STATUSES.includes(maritalStatus);
  const partnerLabel = maritalStatus === 'Married' ? 'spouse' : 'partner';

  function clearPartner() {
    const { partner, ...values } = form.getValues();
    form.reset(
      { ...values, hasPartner: false, partnerEmail: '' },
      { keepDirty: true, keepTouched: true },
    );
  }

  function handleTogglePartner(checked: boolean) {
    if (checked) {
      form.setValue('hasPartner', true, { shouldValidate: true });
      form.setValue('partner.maritalStatus', maritalStatus, { shouldValidate: true });
    } else {
      clearPartner();
    }
  }

  function handleMaritalStatusChange(value: string) {
    form.setValue('maritalStatus', value, { shouldValidate: true });
    if (!PARTNER_ELIGIBLE_STATUSES.includes(value) && hasPartner) {
      clearPartner();
    } else if (hasPartner) {
      form.setValue('partner.maritalStatus', value, { shouldValidate: true });
    }
  }

  function handlePartnerMaritalStatusChange(value: string) {
    form.setValue('partner.maritalStatus', value, { shouldValidate: true });
    form.setValue('maritalStatus', value, { shouldValidate: true });
    if (!PARTNER_ELIGIBLE_STATUSES.includes(value)) {
      clearPartner();
    }
  }

  const mutation = useMutation({
    mutationFn: async (
      formValues: IntakeFormQuickPlanFormValues,
    ): Promise<ShortFormIntakeResponse> => {
      const { sendIntakeForm: send } = formValues;
      const primaryRequest = mapPersonToRequest(
        formValues,
        advisorId,
        send ? formValues.email.trim() : undefined,
      );

      if (formValues.hasPartner && formValues.partner) {
        const partnerRequest = mapPersonToRequest(
          formValues.partner,
          advisorId,
          send ? formValues.partnerEmail.trim() : undefined,
        );
        const coupleResponse = await shortFormIntakeApi.createCouple({
          primary: primaryRequest,
          partner: partnerRequest,
        });
        lastCoupleLinked.current = coupleResponse.linked;
        lastCoupleDeliveryFailed.current = [coupleResponse.primary, coupleResponse.partner].some(
          result => result.intakeFormDeliveryStatus === 'failed',
        );
        return coupleResponse.primary;
      }

      return shortFormIntakeApi.create(primaryRequest);
    },
    onSuccess: (data, variables) => {
      const sent = variables.sendIntakeForm;
      const intakeFormDeliveryFailed = variables.hasPartner
        ? lastCoupleDeliveryFailed.current
        : data.intakeFormDeliveryStatus === 'failed';

      if (intakeFormDeliveryFailed) {
        toast.error('We couldn’t send that email', {
          description: 'Nothing reached your client. Try sending it again.',
        });
      } else if (variables.hasPartner) {
        if (lastCoupleLinked.current) {
          toast.success(
            sent
              ? 'Plans created and intake form sent for both clients'
              : 'Quick plan created for both clients',
          );
        } else {
          toast.warning(
            sent
              ? 'Plans created and intake form sent, but partner linking failed. Please link them manually from your dashboard.'
              : 'Quick plans created for both clients, but linking failed. Please link them manually from your dashboard.',
          );
        }
      } else {
        toast.success(
          sent ? 'Plan created and intake form sent' : 'Quick plan created successfully',
        );
      }

      if (onSuccess) {
        onSuccess(data);
      } else {
        navigate(`/advisors/${advisorId}/clients`);
      }
    },
    onError: (error: unknown) => {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create plan. Please try again.';
      toast.error(errorMessage);
    },
  });

  return {
    form,
    hasPartner,
    canAddPartner,
    partnerLabel,
    sendIntakeForm,
    handleSendIntakeFormChange,
    handleTogglePartner,
    handleMaritalStatusChange,
    handlePartnerMaritalStatusChange,
    onSubmit: form.handleSubmit(data => mutation.mutate(data)),
    isValid: form.formState.isValid,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    data: mutation.data,
    reset: mutation.reset,
  };
}
